// src/pages/HostDashboard.js
import React, { useEffect, useState, useMemo } from 'react';
import { http } from "../lib/axios";
import {
  Box, Container, Typography, Grid, Button,
  Slider, Tabs, Tab, Stack, TextField,
  FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, OutlinedInput, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Snackbar, Alert, IconButton, Tooltip as MuiTooltip
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip, Circle, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import ArtworkCard from '../components/ArtworkCard';
import HostProfileCard from '../components/HostProfileCard';
import ArtistProfileCard from '../components/ArtistProfileCard';
import PayoutsCard from "../components/PayoutsCard";
import { FavoritesSection } from "../components/FavoritesSection";
import MiniArtistCard from "../components/MiniArtistCard";
import MiniArtworkCard from "../components/MiniArtworkCard";
import MiniHostCard from "../components/MiniHostCard";
import ArtworkCreatePage from '../pages/ArtworkCreatePage';
import HostOfferModal from "../components/HostOfferModal";
import ArtworkAgreementEditorModal from "../components/ArtworkAgreementEditorModal";
import PendingAgreementsList from "../components/PendingAgreementsList";
import ArtworkSearchFiltersModal from "../components/ArtworkSearchFiltersModal";
import SalesReportPanel from "../components/SalesReportPanel";
import { approvePaymentRefund, exportHostSalesCsv, getHostSales, rejectPaymentRefund } from "../lib/apiPayments";

import { API_BASE as API_BASE_URL } from "../lib/api";
import subcategorySchema from '../utils/subcategorySchema';
import { geocodeAddress } from '../lib/geocode';
import { useHostHostedArtworks } from "../hooks/useHostHostedArtworks";
import { useChat } from '../context/ChatContext';

const blurredTextSx = {
  display: 'inline-block',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(14px)',
  borderRadius: 1.5,
  px: 1.5,
  py: 0.75,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
  color: '#111827'
};

function toAbsolute(src) {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src;
  const needsSlash = src[0] !== "/";
  return `${API_BASE_URL}${needsSlash ? "/" : ""}${src}`;
}

function normalizeArtworkForCard(item) {
  // item may be HostArtwork or an Artwork
  const a = item?.artwork || item || {};
  const firstImg = Array.isArray(a.images) ? (a.images[0]?.url || a.images[0]?.image || a.images[0]?.image_url) : null;
  const image_url = a.primary_image_url || a.image_url || firstImg || null;
  return { ...a, image_url: toAbsolute(image_url) };
}

const redIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',   // you (host)
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});
const blueIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',  // hosts
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});
const greenIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png', // artists
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});

function MapClickHandler({ onRecenter }) {
  useMapEvents({
    click: (e) => {
      onRecenter([e.latlng.lat, e.latlng.lng]);
    }
  });
  return null;
}

export default function HostDashboard() {
  const { launchChat } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem('access_token');

  // core profile & lists
  const [host, setHost] = useState(null);
  const [position, setPosition] = useState(null);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [agreementModalMode, setAgreementModalMode] = useState("respond");
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerArtwork, setOfferArtwork] = useState(null);
  const [offerContext, setOfferContext] = useState("hosting");
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelAgreement, setCancelAgreement] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // nearby map & lists
  const [distance, setDistance] = useState(20);
  const [nearbyHosts, setNearbyHosts] = useState([]);
  const [nearbyArtists, setNearbyArtists] = useState([]);

  // nearby artworks (merged list from two endpoints)
  const [categoryFilteredArtworks, setCategoryFilteredArtworks] = useState([]);
  const [nearbyArtistArtworks, setNearbyArtistArtworks] = useState([]);
  const [nearbyHostedArtworks, setNearbyHostedArtworks] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState({});
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // gallery / managed artists & artworks
  const [managedArtists, setManagedArtists] = useState([]);
  const [managedArtistsFull, setManagedArtistsFull] = useState([]); // NEW: full artist objects with location
  const [selectedManagedArtistId, setSelectedManagedArtistId] = useState(null);
  const [selectedManagedArtist, setSelectedManagedArtist] = useState(null);
  const [representedManagedArtworks, setRepresentedManagedArtworks] = useState([]);
  const [availableManagedArtworks, setAvailableManagedArtworks] = useState([]);
  const [showAddArtworkModal, setShowAddArtworkModal] = useState(false);

  // modals for creating managed artist
  const [createArtistOpen, setCreateArtistOpen] = useState(false);
  const [newArtistForm, setNewArtistForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    bio: '',
    portfolio_link: '',
    address: '',
  });
  const [newArtistImage, setNewArtistImage] = useState(null);
  const [newArtistError, setNewArtistError] = useState(null);
  const [creatingArtist, setCreatingArtist] = useState(false);
  const [linkArtistOpen, setLinkArtistOpen] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistSearchResults, setArtistSearchResults] = useState([]);
  const [artistSearchLoading, setArtistSearchLoading] = useState(false);
  const [artistSearchError, setArtistSearchError] = useState(null);
  const [linkingArtistId, setLinkingArtistId] = useState(null);
  const [artworkRepresentationSubmittingId, setArtworkRepresentationSubmittingId] = useState(null);

  // modal for full artwork view (managed artworks)
  const [repArtworkModalOpen, setRepArtworkModalOpen] = useState(false);
  const [repArtworkLoading, setRepArtworkLoading] = useState(false);
  const [repSelectedArtworkFull, setRepSelectedArtworkFull] = useState(null);
  const [repSelectedArtworkIsRepresented, setRepSelectedArtworkIsRepresented] = useState(false);

  // tabs
  const [tab, setTab] = useState(0);
  const [nearbySubTab, setNearbySubTab] = useState(0); // 0 artworks, 1 hosts, 2 artists
  const [hostRequestsSubTab, setHostRequestsSubTab] = useState(0); // 0 pending, 1 accepted, 2 rejected, 3 expired, 4 cancelled, 5 sold

  const ARTWORK_STATUSES = ["available", "hosted", "approved", "sold", "featured"];
  const [statusFilterHosted, setStatusFilterHosted] = useState([]); // [] => show all
  const [statusFilterNearby, setStatusFilterNearby] = useState([]); // [] => show all
  const [salesRows, setSalesRows] = useState([]);
  const [salesSummary, setSalesSummary] = useState({});
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState("");
  const [salesYear, setSalesYear] = useState(String(new Date().getFullYear()));
  const [salesActionPaymentId, setSalesActionPaymentId] = useState(null);
  const {
    agreements,
    groupedAgreements,
    hostedArtworks,
    requestedArtworkIds,
    loading: agreementsLoading,
    refreshing: agreementsRefreshing,
    refreshAgreements,
    refreshHostedArtworks,
    createOffer,
    acceptAgreement,
    rejectAgreement,
    counterofferAgreement,
    requestAgreementCancellation,
    confirmAgreementCancellation,
    rejectAgreementCancellation,
    markAgreementSold,
  } = useHostHostedArtworks({ enabled: Boolean(host?.id) });
  const pendingAgreementsLoading = agreementsLoading || agreementsRefreshing;
  const isGalleryHost = !!host && host.venue_type === 'art_gallery';

  // Tab index mapping depends on whether this is a gallery host
  const TAB_PROFILE = 0;
  const TAB_HOSTED = 1;
  const TAB_REPRESENTATIONS = isGalleryHost ? 2 : null;
  const TAB_NEARBY = isGalleryHost ? 3 : 2;
  const TAB_PENDING = isGalleryHost ? 4 : 3;
  const TAB_FAVORITES = isGalleryHost ? 5 : 4;
  const TAB_SALES = isGalleryHost ? 6 : 5;
  const TAB_PAYOUTS = isGalleryHost ? 7 : 6;

  const nearbyAllArtworks = useMemo(() => {
    const a = Array.isArray(nearbyArtistArtworks) ? nearbyArtistArtworks : [];
    const h = Array.isArray(nearbyHostedArtworks) ? nearbyHostedArtworks : [];
    return [...a, ...h];
  }, [nearbyArtistArtworks, nearbyHostedArtworks]);

  const filteredNearbyAllArtworks = useMemo(() => {
    if (!Array.isArray(nearbyAllArtworks)) return [];
    if (statusFilterNearby.length === 0) return nearbyAllArtworks;
    const set = new Set(statusFilterNearby);
    return nearbyAllArtworks.filter(a => set.has(a?.status));
  }, [nearbyAllArtworks, statusFilterNearby]);

  const filteredHostedArtworks = useMemo(() => {
    if (!Array.isArray(hostedArtworks)) return [];
    if (statusFilterHosted.length === 0) return hostedArtworks;
    const set = new Set(statusFilterHosted);
    return hostedArtworks.filter((item) => set.has(item?.artwork?.status ?? item?.status));
  }, [hostedArtworks, statusFilterHosted]);

  const galleryRepresentationAgreementByArtworkId = useMemo(() => {
    const map = new Map();
    agreements.forEach((agreement) => {
      if (
        agreement?.is_gallery_representation &&
        Number(agreement?.host?.id) === Number(host?.id) &&
        agreement?.artwork?.id
      ) {
        map.set(Number(agreement.artwork.id), agreement);
      }
    });
    return map;
  }, [agreements, host?.id]);
  // === LOAD HOST, NEARBY, HOSTED, PENDING, MANAGED ARTISTS ===
  useEffect(() => {
    (async () => {
      try {
        const me = await http.get('/api/accounts/me/');
        const userId = me.data?.id;

        const hostRes = await http.get(`/hosts/?user=${userId}&view=card`);
        if (!hostRes.data?.length) {
          alert('You need to complete your host profile before accessing the dashboard.');
          navigate('/host-profile/setup');
          return;
        }
        const hostProfile = hostRes.data[0];

        const fullProfile = {
          ...hostProfile,
          first_name: hostProfile.user?.first_name,
          last_name: hostProfile.user?.last_name,
          email: hostProfile.user?.email,
        };
        setHost(fullProfile);

        const isGalleryHost = hostProfile.venue_type === 'art_gallery';

        // coordinates for map & nearby fetches
        if (hostProfile?.location) {
          const coords = [hostProfile.location.latitude, hostProfile.location.longitude];
          setPosition(coords);
        }

        // managed artists (for gallery hosts)
        if (isGalleryHost) {
          try {
            await refreshManagedArtists({ hostProfile });
          } catch (e) {
            console.error("Failed to load managed artists", e);
            setManagedArtists([]);
            setManagedArtistsFull([]);
          }
        } else {
          // ensure cleared if not a gallery host
          setManagedArtists([]);
          setManagedArtistsFull([]);
        }
      } catch (err) {
        console.error('Error loading host dashboard', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!position) return;

    const [lat, lon] = position;

    (async () => {
      // nearby hosts
      try {
        const nh = await http.get(
          `/hosts/nearby/?lat=${lat}&lon=${lon}&radius=${distance}&view=card`
        );
        setNearbyHosts(nh.data || []);
      } catch {
        setNearbyHosts([]);
      }

      // nearby artists
      try {
        const na = await http.get(
          `/artists/nearby/?lat=${lat}&lon=${lon}&radius=${distance}&view=card`
        );
        setNearbyArtists(na.data || []);
      } catch {
        setNearbyArtists([]);
      }

      // nearby artworks (both sources)
      try {
        const aa = await http.get(
          `/artworks/nearby_artists/?distance=${distance}&lat=${lat}&lng=${lon}&view=card`
        );
        setNearbyArtistArtworks(aa.data || []);
      } catch {
        setNearbyArtistArtworks([]);
      }
      try {
        const ah = await http.get(
          `/artworks/nearby_hosted/?distance=${distance}&lat=${lat}&lng=${lon}&view=card`
        );
        setNearbyHostedArtworks(ah.data || []);
      } catch {
        setNearbyHostedArtworks([]);
      }
    })();
  }, [position, distance]);

  useEffect(() => {
    if (tab !== TAB_SALES) return;
    let cancelled = false;

    async function loadHostSales() {
      setSalesLoading(true);
      setSalesError("");
      try {
        const data = await getHostSales(`year=${encodeURIComponent(salesYear)}`);
        if (!cancelled) {
          setSalesRows(data.results || []);
          setSalesSummary(data.summary || {});
        }
      } catch (error) {
        if (!cancelled) setSalesError(error.message || "Unable to load host sales.");
      } finally {
        if (!cancelled) setSalesLoading(false);
      }
    }

    loadHostSales();
    return () => {
      cancelled = true;
    };
  }, [tab, TAB_SALES, salesYear]);

  // When selected managed artist changes, load full artist + their artworks
  useEffect(() => {
    if (!selectedManagedArtistId) return;

    (async () => {
      try {
        const artistRes = await http.get(`/artists/${selectedManagedArtistId}/?view=card`);
        const a = artistRes.data;
        const fullArtist = {
          ...a,
          first_name: a.user?.first_name,
          last_name: a.user?.last_name,
          email: a.user?.email,
        };
        setSelectedManagedArtist(fullArtist);
      } catch (e) {
        console.error("Failed to load managed artist detail", e);
        setSelectedManagedArtist(null);
      }

      try {
        const artworksRes = await http.get(`/hosts/managed-artists/${selectedManagedArtistId}/artworks/?view=card`);
        const represented = Array.isArray(artworksRes.data?.represented) ? artworksRes.data.represented : [];
        const available = Array.isArray(artworksRes.data?.available) ? artworksRes.data.available : [];

        if (represented.length > 0 || available.length > 0) {
          setRepresentedManagedArtworks(represented);
          setAvailableManagedArtworks(available);
          return;
        }

        // Fallback for legacy data or partially migrated environments:
        // still show the artist's artworks and infer gallery-created pieces.
        const fallbackRes = await http.get(`/artworks/?artist=${selectedManagedArtistId}&view=card`);
        const allArtworks = Array.isArray(fallbackRes.data) ? fallbackRes.data : [];
        const inferredRepresented = allArtworks.filter(
          (art) => Number(art?.uploaded_by_host?.id) === Number(host?.id)
        );
        const inferredAvailable = allArtworks.filter(
          (art) => Number(art?.uploaded_by_host?.id) !== Number(host?.id)
        );
        setRepresentedManagedArtworks(inferredRepresented);
        setAvailableManagedArtworks(inferredAvailable);
      } catch (e) {
        console.error("Failed to load managed artist artworks", e);
        try {
          const fallbackRes = await http.get(`/artworks/?artist=${selectedManagedArtistId}&view=card`);
          const allArtworks = Array.isArray(fallbackRes.data) ? fallbackRes.data : [];
          const inferredRepresented = allArtworks.filter(
            (art) => Number(art?.uploaded_by_host?.id) === Number(host?.id)
          );
          const inferredAvailable = allArtworks.filter(
            (art) => Number(art?.uploaded_by_host?.id) !== Number(host?.id)
          );
          setRepresentedManagedArtworks(inferredRepresented);
          setAvailableManagedArtworks(inferredAvailable);
        } catch (fallbackError) {
          console.error("Failed to load fallback artist artworks", fallbackError);
          setRepresentedManagedArtworks([]);
          setAvailableManagedArtworks([]);
        }
      }
    })();
  }, [selectedManagedArtistId, host?.id]);

  const goToHostProfile = (id) => navigate(`/hosts/${id}/?view=card`);
  const goToArtistProfile = (id) => navigate(`/artists/${id}/?view=card`);

  const getFullProfile = (profile) => {
    const fullProfile = {
      ...profile,
      first_name: profile.user?.first_name,
      last_name: profile.user?.last_name,
      email: profile.user?.email,
    };
    return fullProfile;
  };

  const handleOfferClick = (artwork, context = "hosting") => {
    setOfferArtwork(artwork);
    setOfferContext(context);
    setOfferModalOpen(true);
  };

  const handleOfferSubmit = async (terms) => {
    if (!offerArtwork?.id) return;
    try {
      setOfferSubmitting(true);
      await createOffer(offerArtwork.id, terms, {
        hostId: host?.id,
        isGalleryRepresentation: offerContext === "galleryRepresentation",
      });
      await refreshAgreements();
      setSnackbar({
        open: true,
        message: offerContext === "galleryRepresentation"
          ? "Gallery representation offer sent to Artist."
          : "Offer sent to Artist.",
        severity: "success"
      });
      setOfferModalOpen(false);
      setOfferArtwork(null);
      setOfferContext("hosting");
    } finally {
      setOfferSubmitting(false);
    }
  };

  const handleAgreementSelect = (agreement, mode = "respond") => {
    if (isPendingCancelStatus(agreement?.status)) {
      handleOpenCancelConfirmation(agreement);
      return;
    }
    setSelectedAgreement(agreement);
    setAgreementModalMode(mode);
    setAgreementModalOpen(true);
  };


  const handleMarkSold = async (artworkId) => {
    if (!artworkId) return;
    try {
      const agreement = agreements.find((item) => Number(item?.artwork?.id) === Number(artworkId));
      let payload = null;
      if (agreement?.id) {
        payload = await markAgreementSold(agreement.id, { artworkId });
      }
      await refreshAgreements();
      await refreshHostedArtworks();
      const saleBreakdown = payload?.sale_breakdown || payload?.data?.sale_breakdown;
      const artistAmount = saleBreakdown?.artist_amount;
      const hostAmount = saleBreakdown?.host_amount;
      const breakdownMessage = (artistAmount !== undefined && hostAmount !== undefined)
        ? `Artwork marked as sold. Artist share: $${artistAmount}. Host share: $${hostAmount}.`
        : "Artwork marked as sold.";
      setSnackbar({ open: true, message: breakdownMessage, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to mark artwork as sold.", severity: "error" });
    }
  };

  const refreshSales = async () => {
    const data = await getHostSales(`year=${encodeURIComponent(salesYear)}`);
    setSalesRows(data.results || []);
    setSalesSummary(data.summary || {});
  };

  const handleApproveRefund = async (row) => {
    const confirmed = window.confirm(`Approve the refund request for ${row?.artwork?.title || "this artwork"}? Stripe will begin processing immediately.`);
    if (!confirmed) return;
    setSalesActionPaymentId(row.payment_id);
    setSalesError("");
    try {
      await approvePaymentRefund(row.payment_id);
      await refreshSales();
      setSnackbar({ open: true, message: "Refund approved and sent to Stripe.", severity: "success" });
    } catch (error) {
      setSalesError(error.message || "Unable to approve the refund.");
    } finally {
      setSalesActionPaymentId(null);
    }
  };

  const handleRejectRefund = async (row) => {
    const rejectionReason = window.prompt(`Why are you rejecting the refund request for ${row?.artwork?.title || "this artwork"}?`);
    if (rejectionReason === null) return;
    if (!rejectionReason.trim()) {
      setSalesError("A rejection reason is required.");
      return;
    }
    const confirmed = window.confirm(`Reject the refund request for ${row?.artwork?.title || "this artwork"}?`);
    if (!confirmed) return;
    setSalesActionPaymentId(row.payment_id);
    setSalesError("");
    try {
      await rejectPaymentRefund(row.payment_id, rejectionReason);
      await refreshSales();
      setSnackbar({ open: true, message: "Refund request rejected.", severity: "success" });
    } catch (error) {
      setSalesError(error.message || "Unable to reject the refund.");
    } finally {
      setSalesActionPaymentId(null);
    }
  };

  const handleExportSalesCsv = async () => {
    try {
      const result = await exportHostSalesCsv(`year=${encodeURIComponent(salesYear)}`);
      const csvText = typeof result === "string" ? result : (result?.raw || "");
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `host-sales-${salesYear}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setSalesError(error.message || "Unable to export the sales report.");
    }
  };

  const handleRequestAgreementCancellation = async (agreement) => {
    if (!agreement?.id) return;
    try {
      await requestAgreementCancellation(agreement.id);
      await refreshAgreements();
      setSnackbar({ open: true, message: "Cancellation request sent.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to request cancellation.", severity: "error" });
    }
  };

  const isPendingCancelStatus = (status) => {
    const normalized = String(status || "").toUpperCase();
    return normalized === "PENDING_CANCEL_OK_HOST" || normalized === "PENDING_CANCEL_OK_ARTIST";
  };

  const handleOpenCancelConfirmation = (agreement) => {
    setCancelAgreement(agreement);
    setCancelConfirmOpen(true);
  };

  const handleCancelConfirmationClose = () => {
    if (cancelSubmitting) return;
    setCancelConfirmOpen(false);
    setCancelAgreement(null);
  };

  const handleConfirmAgreementCancel = async () => {
    if (!cancelAgreement?.id) return;
    try {
      setCancelSubmitting(true);
      await confirmAgreementCancellation(cancelAgreement.id);
      await refreshAgreements();
      await refreshHostedArtworks();
      setSnackbar({ open: true, message: "Agreement cancelled.", severity: "success" });
      setCancelConfirmOpen(false);
      setCancelAgreement(null);
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to confirm cancellation.", severity: "error" });
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleRejectAgreementCancel = async () => {
    if (!cancelAgreement?.id) return;
    try {
      setCancelSubmitting(true);
      await rejectAgreementCancellation(cancelAgreement.id);
      await refreshAgreements();
      setSnackbar({ open: true, message: "Cancellation request rejected.", severity: "success" });
      setCancelConfirmOpen(false);
      setCancelAgreement(null);
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to reject cancellation.", severity: "error" });
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleAcceptAgreement = async (agreementId) => {
    await acceptAgreement(agreementId);
    await refreshAgreements();
    await refreshHostedArtworks();
    setSnackbar({ open: true, message: "Signed.", severity: "success" });
  };

  const handleRejectAgreement = async (agreementId) => {
    await rejectAgreement(agreementId);
    await refreshAgreements();
    setSnackbar({ open: true, message: "Request rejected.", severity: "success" });
  };

  // Filter nearby artworks by category/subcategories
  useEffect(() => {
    const allArtworks = filteredNearbyAllArtworks;

    if (allArtworks) {
      const filtered = allArtworks.filter(art => {
        if (selectedCategory && art.artwork_type !== selectedCategory) return false;
        const numericPrice = Number(art?.price);
        if (minPrice !== '' && (!Number.isFinite(numericPrice) || numericPrice < Number(minPrice))) return false;
        if (maxPrice !== '' && (!Number.isFinite(numericPrice) || numericPrice > Number(maxPrice))) return false;
        if (selectedCategory && subcategorySchema[selectedCategory]) {
          for (let subKey of Object.keys(subcategorySchema[selectedCategory])) {
            if (selectedSubcategories[subKey] && art.subcategories?.[subKey] !== selectedSubcategories[subKey]) {
              return false;
            }
          }
        }
        return true;
      });

      setCategoryFilteredArtworks(filtered);
    }
  }, [filteredNearbyAllArtworks, selectedCategory, selectedSubcategories, minPrice, maxPrice]);

  const refreshManagedArtists = async ({ hostProfile = host, preferredArtistId = null } = {}) => {
    if (!hostProfile || hostProfile.venue_type !== 'art_gallery') {
      setManagedArtists([]);
      setManagedArtistsFull([]);
      setSelectedManagedArtistId(null);
      return [];
    }

    const listRes = await http.get('/hosts/managed-artists/');
    const list = Array.isArray(listRes.data) ? listRes.data : [];
    setManagedArtists(list);

    const artistIds = [...new Set(list.map((link) => link.artist || link.artist_id).filter(Boolean))];
    if (artistIds.length === 0) {
      setManagedArtistsFull([]);
      setSelectedManagedArtistId(null);
      return list;
    }

    const fullResponses = await Promise.all(
      artistIds.map((id) => http.get(`/artists/${id}/?view=card`).catch(() => null))
    );
    const fullArtists = fullResponses.map((res) => res?.data).filter(Boolean);
    setManagedArtistsFull(fullArtists);

    const desiredId = preferredArtistId ?? selectedManagedArtistId;
    const nextSelectedId = artistIds.find((id) => String(id) === String(desiredId)) || artistIds[0] || null;
    setSelectedManagedArtistId(nextSelectedId);
    return list;
  };

  // ---- Handlers for managed artist creation ----
  const handleNewArtistChange = (e) => {
    const { name, value } = e.target;
    setNewArtistForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateArtist = async () => {
    setNewArtistError(null);

    try {
      setCreatingArtist(true);

      // 1) Attempt to geocode the address (so artist gets a location point)
      let locationPayload = null;
      if (newArtistForm.address) {
        try {
          const data = await geocodeAddress(newArtistForm.address);
          const firstResult = data?.results?.[0];
          if (firstResult?.geometry) {
            const { lat, lng } = firstResult.geometry;
            locationPayload = {
              latitude: lat,
              longitude: lng,
            };
          }
        } catch (geoErr) {
          console.error('Failed to geocode artist address', geoErr);
          // We allow creation even if geocoding fails; artist just won't have a location.
        }
      }

      // 2) Build multipart FormData so we can include a profile image
      const formData = new FormData();
      Object.entries(newArtistForm).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      if (locationPayload) {
        formData.append('location', JSON.stringify(locationPayload));
      }

      if (newArtistImage) {
        formData.append('profile_image', newArtistImage);
      }

      // 3) POST to /hosts/managed-artists/ (HostCreateArtistSerializer will handle it)
      const createdRes = await http.post('/hosts/managed-artists/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const createdArtistId = createdRes?.data?.id;

      // 4) Reset and refresh the list
      setCreateArtistOpen(false);
      setNewArtistForm({
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        bio: '',
        portfolio_link: '',
        address: '',
      });
      setNewArtistImage(null);

      await refreshManagedArtists({ preferredArtistId: createdArtistId });
      setSnackbar({ open: true, message: 'Represented artist created and invited.', severity: 'success' });
    } catch (e) {
      console.error('Failed to create managed artist', e);
      setNewArtistError('Failed to create artist. Please check the form and try again.');
    } finally {
      setCreatingArtist(false);
    }
  };

  const refreshSelectedManagedArtistArtworks = async (artistId = selectedManagedArtistId) => {
    if (!artistId) {
      setRepresentedManagedArtworks([]);
      setAvailableManagedArtworks([]);
      return;
    }

    const { data } = await http.get(`/hosts/managed-artists/${artistId}/artworks/?view=card`);
    setRepresentedManagedArtworks(Array.isArray(data?.represented) ? data.represented : []);
    setAvailableManagedArtworks(Array.isArray(data?.available) ? data.available : []);
  };

  const handleAddExistingArtworkToRepresentation = async (artwork) => {
    if (!selectedManagedArtistId || !artwork?.id) return;
    try {
      setArtworkRepresentationSubmittingId(artwork.id);
      handleOfferClick(artwork, "galleryRepresentation");
    } catch (error) {
      console.error('Failed to add artwork to representation', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || error?.response?.data?.error || 'Unable to add artwork to representation.',
        severity: 'error',
      });
    } finally {
      setArtworkRepresentationSubmittingId(null);
    }
  };

  const handleRemoveExistingArtworkFromRepresentation = async (artwork) => {
    if (!artwork?.id) return;
    try {
      setArtworkRepresentationSubmittingId(artwork.id);
      await http.delete(`/hosts/managed-artworks/${artwork.id}/unlink/`);
      await refreshSelectedManagedArtistArtworks(selectedManagedArtistId);
      setSnackbar({ open: true, message: 'Artwork removed from gallery representation.', severity: 'success' });
    } catch (error) {
      console.error('Failed to remove artwork from representation', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || error?.response?.data?.error || 'Unable to remove artwork from representation.',
        severity: 'error',
      });
    } finally {
      setArtworkRepresentationSubmittingId(null);
    }
  };

  // ---- Full artwork viewer for managed artworks ----
  const handleManagedArtworkClick = async (id, isRepresented = false) => {
    setRepArtworkModalOpen(true);
    setRepArtworkLoading(true);
    setRepSelectedArtworkFull(null);
    setRepSelectedArtworkIsRepresented(isRepresented);
    try {
      const { data } = await http.get(`/artworks/${id}/`, { params: { view: "card" } });
      setRepSelectedArtworkFull(data);
    } catch (e) {
      console.error("Failed to load artwork detail", e);
    } finally {
      setRepArtworkLoading(false);
    }
  };

  const handleManagedArtworkDeleted = (deletedId) => {
    setRepresentedManagedArtworks(prev => prev.filter(a => a.id !== deletedId));
    setAvailableManagedArtworks(prev => prev.filter(a => a.id !== deletedId));
    setRepArtworkModalOpen(false);
  };

  const handleSearchExistingArtists = async () => {
    const query = artistSearchQuery.trim();
    if (!query) {
      setArtistSearchResults([]);
      setArtistSearchError('Enter a name, username, or email to search for an artist.');
      return;
    }

    try {
      setArtistSearchLoading(true);
      setArtistSearchError(null);

      const searchRes = await http.get('/api/accounts/search/', {
        params: { username: query },
      });
      const userMatches = Array.isArray(searchRes.data) ? searchRes.data : [];
      if (userMatches.length === 0) {
        setArtistSearchResults([]);
        setArtistSearchError('No matching users were found.');
        return;
      }

      const artistMatches = await Promise.all(
        userMatches.map(async (match) => {
          try {
            const { data } = await http.get('/artists/', { params: { user: match.id } });
            const artist = Array.isArray(data) ? data[0] : null;
            if (!artist?.id) return null;
            const detailRes = await http.get(`/artists/${artist.id}/?view=card`).catch(() => ({ data: artist }));
            const fullArtist = detailRes?.data || artist;
            return {
              ...fullArtist,
              first_name: fullArtist.user?.first_name || match.first_name || '',
              last_name: fullArtist.user?.last_name || match.last_name || '',
              email: fullArtist.user?.email || '',
            };
          } catch (_error) {
            return null;
          }
        })
      );

      const managedIds = new Set(managedArtists.map((link) => String(link.artist || link.artist_id)));
      const linkableArtists = artistMatches.filter(
        (artist) => artist?.id && !managedIds.has(String(artist.id))
      );

      setArtistSearchResults(linkableArtists);
      if (linkableArtists.length === 0) {
        setArtistSearchError('No linkable artist profiles matched that search.');
      }
    } catch (error) {
      console.error('Failed to search existing artists', error);
      setArtistSearchResults([]);
      setArtistSearchError('Unable to search artists right now.');
    } finally {
      setArtistSearchLoading(false);
    }
  };

  const handleLinkExistingArtist = async (artist) => {
    if (!artist?.id) return;
    try {
      setLinkingArtistId(artist.id);
      setArtistSearchError(null);

      await http.post('/hosts/managed-artists/link-existing/', {
        artist_id: artist.id,
        display_name:
          `${artist.first_name || ''} ${artist.last_name || ''}`.trim() ||
          artist.user?.username ||
          '',
      });

      await refreshManagedArtists({ preferredArtistId: artist.id });
      setLinkArtistOpen(false);
      setArtistSearchQuery('');
      setArtistSearchResults([]);
      setSnackbar({ open: true, message: 'Existing artist linked to your gallery.', severity: 'success' });
    } catch (error) {
      console.error('Failed to link existing artist', error);
      setArtistSearchError(error?.response?.data?.error || 'Unable to link this artist.');
    } finally {
      setLinkingArtistId(null);
    }
  };

  const handleDeleteManagedArtist = async (artist) => {
    if (!artist?.id) return;
    const displayName =
      `${artist.first_name || ''} ${artist.last_name || ''}`.trim() ||
      artist.user?.username ||
      'this artist';
    const confirmed = window.confirm(`Remove ${displayName} from your represented artists list?`);
    if (!confirmed) return;

    try {
      await http.delete(`/hosts/managed-artists/${artist.id}/unlink/`);
      await refreshManagedArtists();
      setRepresentedManagedArtworks([]);
      setAvailableManagedArtworks([]);
      setSelectedManagedArtist(null);
      setSnackbar({ open: true, message: 'Represented artist removed.', severity: 'success' });
    } catch (error) {
      console.error('Failed to remove represented artist', error);
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Unable to remove this represented artist.';
      setSnackbar({ open: true, message: detail, severity: 'error' });
    }
  };

  // ---- NEW: combine nearby artists + managed artists for map & "Nearby Artists" tab ----
  const combinedArtistsForMap = useMemo(() => {
    const base = Array.isArray(nearbyArtists) ? nearbyArtists : [];
    const managed = Array.isArray(managedArtistsFull) ? managedArtistsFull : [];

    const merged = [...base, ...managed];
    const seen = new Set();
    return merged.filter(a => {
      if (!a || !a.id) return false;
      if (!a.location) return false; // skip artists with no location
      if (seen.has(a.id)) return false;
      seen.add(a.id);
      return true;
    });
  }, [nearbyArtists, managedArtistsFull]);

  // Filter out the current host from nearby hosts so we don't double-show them
  const filteredNearbyHosts = useMemo(() => {
    if (!Array.isArray(nearbyHosts)) return [];
    if (!host?.id) return nearbyHosts;
    return nearbyHosts.filter(h => h.id !== host.id);
  }, [nearbyHosts, host]);

  if (!host) return <Typography sx={blurredTextSx}>Loading...</Typography>;

  return (
    <Container sx={{ mt: 5 }}>
      {/* Title */}
      <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom align="center" sx={blurredTextSx}>
          {host.user?.first_name} {host.user?.last_name}'s Dashboard
        </Typography>
      </Box>

      {/* Tabs under title */}
      <Box
        sx={{
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(14px)',
          px: 1.5,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <MuiTooltip title="Help for host dashboard">
          <IconButton
            component={RouterLink}
            to="/help"
            state={{ backgroundLocation: location, helpSection: 'host-help' }}
            size="small"
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              color: '#111827',
              fontWeight: 800,
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
            }}
          >
            ?
          </IconButton>
        </MuiTooltip>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 44,
              fontWeight: 600,
              borderRadius: 1.5,
              border: '1px solid transparent',
            },
            '& .Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
            }
          }}
        >
          <Tab label="My Profile" />
          <Tab label="My Hosted ARTflow Artworks" />
          {isGalleryHost && <Tab label="My Art Representations" />}
          <Tab label="Nearby Art" />
          <Tab label="Manage Hosted Artworks" />
          <Tab label="My Favorites" />
          <Tab label="My Sales" />
          <Tab label="My Payouts" />
        </Tabs>
      </Box>

      {/* My Profile */}
      {tab === TAB_PROFILE && (
        <Box sx={{ mt: 2 }}>
          <HostProfileCard user={host} isSelf={true} />
        </Box>
      )}

      {/* My Hosted ARTflow Artworks */}
      {tab === TAB_HOSTED && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}>
            <FormControl
              size="small"
              sx={{
                minWidth: 260,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(14px)',
                  borderRadius: 2,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                }
              }}
            >
              <InputLabel id="hosted-artwork-status-filter-label">Filter by status</InputLabel>
              <Select
                labelId="hosted-artwork-status-filter-label"
                multiple
                value={statusFilterHosted}
                onChange={(e) => setStatusFilterHosted(e.target.value)}
                input={<OutlinedInput label="Filter by status" />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? "All statuses"
                    : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => <Chip key={value} label={value} />)}
                      </Box>
                    )
                }
              >
                {ARTWORK_STATUSES.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox checked={statusFilterHosted.indexOf(name) > -1} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {statusFilterHosted.length > 0 && (
              <Button variant="text" onClick={() => setStatusFilterHosted([])}>
                Clear
              </Button>
            )}

            {filteredHostedArtworks.length > 0 && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ...blurredTextSx, ml: 'auto' }}
              >
                ({filteredHostedArtworks.length} shown)
              </Typography>
            )}
          </Box>

          <Grid container spacing={3}>
            {filteredHostedArtworks.map((item) => {
              const art = normalizeArtworkForCard(item);
              const relatedAgreement = galleryRepresentationAgreementByArtworkId.get(Number(art?.id));
              const canGalleryMarkSold =
                isGalleryHost &&
                (
                  Number(art?.uploaded_by_host?.id) === Number(host?.id) ||
                  Boolean(relatedAgreement)
                );
              return (
                <Grid item xs={12} md={6} lg={4} key={art.id}>
                  <ArtworkCard
                    art={art}
                    showActions
                    onMarkSold={handleMarkSold}
                    allowMarkSold={canGalleryMarkSold}
                    canManageOverride={canGalleryMarkSold}
                  />
                </Grid>
              );
            })}
          </Grid>

          {filteredHostedArtworks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ ...blurredTextSx, mt: 1 }}>
              No hosted artworks match the selected status{statusFilterHosted.length > 1 ? "es" : ""}.
            </Typography>
          )}
        </Box>
      )}

      {/* My Art Representations (gallery hosts only) */}
      {isGalleryHost && tab === TAB_REPRESENTATIONS && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* My Artists slider */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1, ...blurredTextSx }}>
              My Artists
            </Typography>
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(14px)',
                borderRadius: 2,
                boxShadow: '0 8px 18px rgba(0,0,0,0.08)',
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 1
                }}
              >
                {managedArtists.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    You are not representing any artists yet.
                  </Typography>
                )}
                {managedArtists.map((link) => {
                  const id = link.artist || link.artist_id;
                  if (!id) return null;
                  return (
                    <Box key={link.id} sx={{ minWidth: 260, flex: '0 0 auto' }}>
                      <MiniArtistCard
                        id={id}
                        onViewProfile={() => setSelectedManagedArtistId(id)}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ mt: 2, textAlign: 'left' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={() => setCreateArtistOpen(true)}
                  sx={{
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#1d4ed8' }
                  }}
                >
                  Add New Artist
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setArtistSearchError(null);
                    setLinkArtistOpen(true);
                  }}
                  sx={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(239,246,255,0.92) 100%)',
                    color: '#1d4ed8',
                    borderColor: 'rgba(37, 99, 235, 0.35)',
                    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.14)',
                    '&:hover': {
                      background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(219,234,254,0.98) 100%)',
                      borderColor: 'rgba(37, 99, 235, 0.5)',
                    },
                  }}
                >
                  Link Existing Artist
                </Button>
              </Stack>
            </Box>

            {/* Full Artist Profile (selected managed artist) */}
            {selectedManagedArtist && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, ...blurredTextSx }}>
                  Selected Artist Profile
                </Typography>
                <ArtistProfileCard
                  user={selectedManagedArtist}
                  canEditManaged={true}
                  onDeleteArtist={() => handleDeleteManagedArtist(selectedManagedArtist)}
                />
              </Box>
            )}
          </Box>

          {/* My Artworks slider for selected artist */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1, ...blurredTextSx }}>
              Artwork Representation Inventory
            </Typography>
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(14px)',
                borderRadius: 2,
                boxShadow: '0 8px 18px rgba(0,0,0,0.08)',
                p: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  color: '#111827',
                  display: 'inline-block',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.82)',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                }}
              >
                Represented by This Gallery
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 1,
                  mb: 2,
                }}
              >
                {(!selectedManagedArtistId || representedManagedArtworks.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedManagedArtistId
                      ? "No artworks are currently represented by this gallery for the selected artist."
                      : "Select an artist above to see represented artworks."}
                  </Typography>
                )}
                {representedManagedArtworks.map((art) => {
                  const isGalleryCreated = Number(art?.uploaded_by_host?.id) === Number(host?.id);
                  return (
                    <Box key={art.id} sx={{ minWidth: 260, flex: '0 0 auto' }}>
                      <MiniArtworkCard
                        artwork={art}
                        onClick={() => handleManagedArtworkClick(art.id, true)}
                      />
                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {isGalleryCreated ? 'Created by this gallery' : 'Linked existing artwork'}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={isGalleryCreated || artworkRepresentationSubmittingId === art.id}
                          onClick={() => handleRemoveExistingArtworkFromRepresentation(art)}
                        >
                          {isGalleryCreated ? 'Gallery-owned artwork' : artworkRepresentationSubmittingId === art.id ? 'Removing…' : 'Remove from Representation'}
                        </Button>
                      </Stack>
                  </Box>
                );
              })}
            </Box>

              <Box
                sx={{
                  my: 2.75,
                  height: 0,
                  width: '100%',
                  borderTop: '2px solid rgba(15, 23, 42, 0.28)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.55)',
                  boxShadow: '0 1px 0 rgba(15, 23, 42, 0.08)',
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  letterSpacing: 0.1,
                  color: '#4b5563',
                  lineHeight: 1.55,
                  maxWidth: '48rem',
                }}
              >
                Only artworks the artist explicitly marked as gallery represented appear here for claim and negotiation.
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  color: '#111827',
                  display: 'inline-block',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.82)',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                }}
              >
                Gallery-Eligible Artist Artworks
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  pb: 1,
                }}
              >
                {(!selectedManagedArtistId || availableManagedArtworks.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedManagedArtistId
                      ? "No gallery-represented artworks are currently awaiting a gallery claim for the selected artist."
                      : "Select an artist above to see gallery-eligible artworks."}
                  </Typography>
                )}
                {availableManagedArtworks.map((art) => (
                  <Box key={art.id} sx={{ minWidth: 260, flex: '0 0 auto' }}>
                    <MiniArtworkCard
                      artwork={art}
                      onClick={() => handleManagedArtworkClick(art.id, false)}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ mt: 1 }}
                      disabled={artworkRepresentationSubmittingId === art.id || requestedArtworkIds.includes(art.id)}
                      onClick={() => handleAddExistingArtworkToRepresentation(art)}
                    >
                      {requestedArtworkIds.includes(art.id)
                        ? 'Representation Pending'
                        : artworkRepresentationSubmittingId === art.id
                          ? 'Adding…'
                          : 'Add to Representation'}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 2, textAlign: 'left' }}>
              <Button
                variant="contained"
                onClick={() => setShowAddArtworkModal(true)}
                sx={{
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#1d4ed8' }
                }}
                disabled={!selectedManagedArtistId}
              >
                Add New Artwork
              </Button>
              {!selectedManagedArtistId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Select an artist above before adding an artwork.
                </Typography>
              )}
            </Box>

          </Box>
        </Box>
      )}

      {/* Nearby Art: map + sub-tabs */}
      {tab === TAB_NEARBY && (
        <Box sx={{ mt: 2 }}>
          {/* Radius control */}
          <Box width="100%" maxWidth="600px" mx="auto" my={2}>
            <Typography gutterBottom sx={blurredTextSx}>Search radius: {distance} km</Typography>
            <Slider
              value={distance}
              onChange={(e, v) => setDistance(v)}
              min={1}
              max={200}
              valueLabelDisplay="auto"
            />
          </Box>

          {/* Map */}
          {host.location && position && (
            <MapContainer
              center={position}
              zoom={10}
              style={{ height: '400px', marginBottom: '12px' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapClickHandler onRecenter={setPosition} />
              {/* You (host) */}
              <Marker
                position={[host.location.latitude, host.location.longitude]}
                icon={redIcon}
              >
                <Tooltip>{host.user?.first_name} {host.user?.last_name} (you)</Tooltip>
                <Popup>
                  <MiniHostCard id={host.id} />
                  <Stack direction="row" spacing={1} mt={1}>
                    <Button size="small" variant="outlined" onClick={() => goToHostProfile(host.id)}>
                      View Profile
                    </Button>
                  </Stack>
                </Popup>
              </Marker>
              <Circle center={position} radius={distance * 1000} pathOptions={{ color: 'blue' }} />

              {/* Hosts (excluding yourself) */}
              {filteredNearbyHosts.map((h) => (
                h.location && (
                  <Marker
                    key={`h-${h.id}`}
                    position={[h.location.latitude, h.location.longitude]}
                    icon={blueIcon}
                  >
                    <Tooltip>{h.display_name || `${h.user?.first_name || ''} ${h.user?.last_name || ''}`.trim()}</Tooltip>
                    <Popup>
                      <MiniHostCard id={h.id} />
                      <Stack direction="row" spacing={1} mt={1}>
                        <Button size="small" variant="outlined" onClick={() => goToHostProfile(h.id)}>
                          View Profile
                        </Button>
                        {isAuthenticated && (
                          <Button size="small" variant="contained" onClick={() => launchChat(h)}>
                            Message
                          </Button>
                        )}
                      </Stack>
                    </Popup>
                  </Marker>
                )
              ))}

              {/* Artists: nearby + managed, with their own locations */}
              {combinedArtistsForMap.map((a) => (
                a.location && (
                  <Marker
                    key={`a-${a.id}`}
                    position={[a.location.latitude, a.location.longitude]}
                    icon={greenIcon}
                  >
                    <Tooltip>{a.user?.first_name} {a.user?.last_name}</Tooltip>
                    <Popup>
                      <MiniArtistCard id={a.id} />
                      <Stack direction="row" spacing={1} mt={1}>
                        <Button size="small" variant="outlined" onClick={() => goToArtistProfile(a.id)}>
                          View Profile
                        </Button>
                        {isAuthenticated && (
                          <Button size="small" variant="contained" onClick={() => launchChat(a)}>
                            Message
                          </Button>
                        )}
                      </Stack>
                    </Popup>
                  </Marker>
                )
              ))}
            </MapContainer>
          )}

          {/* Sub-tabs: Nearby Artworks / Nearby Hosts / Nearby Artists */}
          <Box
            sx={{
              borderRadius: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(14px)',
              px: 1,
              py: 0.5,
              mb: 1
            }}
          >
            <Tabs
              value={nearbySubTab}
              onChange={(e, v) => setNearbySubTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontWeight: 600, borderRadius: 1.5, border: '1px solid transparent' },
                '& .Mui-selected': { backgroundColor: 'rgba(255, 255, 255, 0.4)' }
              }}
            >
              <Tab label="Nearby Artworks" />
              <Tab label="Nearby Hosts" />
              <Tab label="Nearby Artists" />
            </Tabs>
          </Box>

          {/* Nearby Artworks (merged artist+hosted lists) */}
          {nearbySubTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setFilterModalOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    color: '#111827',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      borderColor: 'rgba(255, 255, 255, 0.75)',
                    },
                  }}
                >
                  Search Filters
                </Button>
                {categoryFilteredArtworks.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, ...blurredTextSx }}>
                    ({categoryFilteredArtworks.length} shown)
                  </Typography>
                )}
              </Box>

              <Grid container spacing={3}>
                {categoryFilteredArtworks.map((art) => (
                  <Grid item xs={12} md={6} lg={4} key={art.id}>
                    <ArtworkCard
                      art={art}
                      showActions={true}
                      {...(art.status === 'available' && {
                        customActionButton: {
                          label: requestedArtworkIds.includes(art.id) ? 'Offer Pending' : 'Request to Host',
                          onClick: () => handleOfferClick(art),
                          disabled: requestedArtworkIds.includes(art.id),
                        }
                      })}
                    />
                  </Grid>
                ))}
              </Grid>

              {categoryFilteredArtworks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ...blurredTextSx }}>
                  No artworks match the selected status{statusFilterNearby.length > 1 ? "es" : ""}.
                </Typography>
              )}
            </Box>
          )}

          {/* Nearby Hosts */}
          {nearbySubTab === 1 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {filteredNearbyHosts.map((h) => (
                  <Grid item xs={12} md={6} lg={4} key={h.id}>
                    <HostProfileCard user={getFullProfile(h)} isSelf={false} onStartChat={launchChat} />
                  </Grid>
                ))}
                {filteredNearbyHosts.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2, ...blurredTextSx }}>
                    No other hosts found in this radius.
                  </Typography>
                )}
              </Grid>
            </Box>
          )}

          {/* Nearby Artists: nearby + managed */}
          {nearbySubTab === 2 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {combinedArtistsForMap.map((a) => (
                  <Grid item xs={12} md={6} lg={4} key={a.id}>
                    <ArtistProfileCard user={getFullProfile(a)} isSelf={false} onStartChat={launchChat} />
                  </Grid>
                ))}
                {combinedArtistsForMap.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2, ...blurredTextSx }}>
                    No artists found in this radius (and no represented artists with known locations).
                  </Typography>
                )}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* My Hosting Requests */}
      {tab === TAB_PENDING && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              borderRadius: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(14px)',
              px: 1,
              py: 0.5,
              mb: 1
            }}
          >
            <Tabs
              value={hostRequestsSubTab}
              onChange={(e, v) => setHostRequestsSubTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { textTransform: 'none', minHeight: 40, borderRadius: 1.5, border: '1px solid transparent', fontWeight: 600 },
                '& .Mui-selected': { backgroundColor: 'rgba(255, 255, 255, 0.4)'}
              }}
            >
              <Tab label="Pending" />
              <Tab label="Accepted" />
              <Tab label="Rejected" />
              <Tab label="Expired" />
              <Tab label="Cancelled" />
              <Tab label="Sold" />
            </Tabs>
          </Box>
          {pendingAgreementsLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading hosting requests...</Typography>
            </Box>
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 0 && (
            <PendingAgreementsList
              agreements={groupedAgreements.pending}
              onSelect={handleAgreementSelect}
              roleLabel="host"
              emptyLabel="No pending hosting requests right now."
              renderActions={(agreement) => (
                isPendingCancelStatus(agreement?.status)
                  ? [{
                    key: "review-cancel-request",
                    label: "Review Cancel Request",
                    onClick: () => handleOpenCancelConfirmation(agreement),
                  }]
                  : []
              )}
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 1 && (
            <PendingAgreementsList
              agreements={groupedAgreements.accepted}
              roleLabel="host"
              emptyLabel="No accepted hosting requests yet."
              renderActions={(agreement) => ([
                {
                  key: "change-terms",
                  label: "Change Agreement Terms",
                  onClick: () => handleAgreementSelect(agreement, "changeTerms"),
                },
                {
                  key: "request-cancel",
                  label: "Request to Cancel Agreement",
                  onClick: () => handleRequestAgreementCancellation(agreement),
                },
              ])}
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 2 && (
            <PendingAgreementsList
              agreements={groupedAgreements.rejected}
              roleLabel="host"
              emptyLabel="No rejected hosting requests."
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 3 && (
            <PendingAgreementsList
              agreements={groupedAgreements.expired}
              roleLabel="host"
              emptyLabel="No expired hosting requests."
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 4 && (
            <PendingAgreementsList
              agreements={groupedAgreements.cancelled}
              roleLabel="host"
              emptyLabel="No cancelled hosting requests."
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 5 && (
            <PendingAgreementsList
              agreements={groupedAgreements.sold}
              roleLabel="host"
              emptyLabel="No sold hosting agreements."
            />
          )}
        </Box>
      )}

      {/* My Favorites */}
      {tab === TAB_FAVORITES && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FavoritesSection type="artworks" />
          <FavoritesSection type="artists" />
          <FavoritesSection type="hosts" />
        </Box>
      )}

      {tab === TAB_SALES && (
        <Box sx={{ mt: 2 }}>
          <SalesReportPanel
            title="Host Sales"
            roleLabel="Host"
            rows={salesRows}
            summary={salesSummary}
            loading={salesLoading}
            error={salesError}
            year={salesYear}
            onYearChange={setSalesYear}
            onExportCsv={handleExportSalesCsv}
            onApproveRefund={handleApproveRefund}
            onRejectRefund={handleRejectRefund}
            actingPaymentId={salesActionPaymentId}
          />
        </Box>
      )}

      {/* My Payouts */}
      {tab === TAB_PAYOUTS && (
        <Box sx={{ mt: 2 }}>
          <PayoutsCard roleLabel="Host" />
        </Box>
      )}

      <HostOfferModal
        open={offerModalOpen}
        onClose={() => {
          setOfferModalOpen(false);
          setOfferArtwork(null);
          setOfferContext("hosting");
        }}
        artwork={offerArtwork}
        onSubmit={handleOfferSubmit}
        submitting={offerSubmitting}
        title={offerContext === "galleryRepresentation" ? "Propose Gallery Representation" : "Request to Host"}
        submitLabel={offerContext === "galleryRepresentation" ? "Send Representation Offer" : "Send Offer"}
        introText={
          offerContext === "galleryRepresentation"
            ? "This sends a formal gallery representation agreement for the artist to review, reject, or counteroffer. Gallery-represented sales currently carry no platform fee."
            : "Platform fee is separate and not part of this split."
        }
      />

      <ArtworkAgreementEditorModal
        open={agreementModalOpen}
        onClose={() => setAgreementModalOpen(false)}
        agreement={selectedAgreement}
        mode={agreementModalMode}
        onAccept={handleAcceptAgreement}
        onReject={handleRejectAgreement}
        onCounteroffer={counterofferAgreement}
      />

      <Dialog open={cancelConfirmOpen} onClose={handleCancelConfirmationClose}>
        <DialogTitle>Cancellation Confirmation Requested</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The other party requested to cancel this accepted agreement. Accept cancellation to move this agreement to CANCELLED, or reject to keep it ACCEPTED.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmationClose} disabled={cancelSubmitting}>Close</Button>
          <Button variant="outlined" onClick={handleRejectAgreementCancel} disabled={cancelSubmitting}>Reject Cancellation</Button>
          <Button variant="contained" color="error" onClick={handleConfirmAgreementCancel} disabled={cancelSubmitting}>Accept Cancellation</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* --- Modal: Add New Artist --- */}
      <Dialog
        open={createArtistOpen}
        onClose={() => setCreateArtistOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Artist</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="First Name"
              name="first_name"
              value={newArtistForm.first_name}
              onChange={handleNewArtistChange}
              fullWidth
            />
            <TextField
              label="Last Name"
              name="last_name"
              value={newArtistForm.last_name}
              onChange={handleNewArtistChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              value={newArtistForm.email}
              onChange={handleNewArtistChange}
              fullWidth
            />
            <TextField
              label="Username"
              name="username"
              value={newArtistForm.username}
              onChange={handleNewArtistChange}
              fullWidth
            />
            <TextField
              label="Bio"
              name="bio"
              value={newArtistForm.bio}
              onChange={handleNewArtistChange}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Portfolio Link"
              name="portfolio_link"
              value={newArtistForm.portfolio_link}
              onChange={handleNewArtistChange}
              fullWidth
            />

            {/* NEW: Address (for geocoding) */}
            <TextField
              label="Address"
              name="address"
              value={newArtistForm.address}
              onChange={handleNewArtistChange}
              fullWidth
            />

            {/* NEW: Profile image upload */}
            <Button
              variant="outlined"
              component="label"
              sx={{ mt: 1 }}
            >
              Upload Profile Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setNewArtistImage(e.target.files[0]);
                  }
                }}
              />
            </Button>
            {newArtistImage && (
              <Typography variant="caption" color="text.secondary">
                Selected: {newArtistImage.name}
              </Typography>
            )}

            {newArtistError && (
              <Typography variant="caption" color="error">
                {newArtistError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateArtistOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateArtist}
            disabled={creatingArtist}
            sx={{ backgroundColor: '#2563eb', color: '#ffffff', '&:hover': { backgroundColor: '#1d4ed8' } }}
          >
            {creatingArtist ? 'Saving…' : 'Save Artist'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linkArtistOpen}
        onClose={() => {
          if (!linkingArtistId) setLinkArtistOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Link Existing Artist</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Search for an existing artist account by name, username, or email, then link that artist to your gallery.
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                fullWidth
                label="Search artists"
                value={artistSearchQuery}
                onChange={(e) => setArtistSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchExistingArtists();
                  }
                }}
              />
              <Button variant="contained" onClick={handleSearchExistingArtists} disabled={artistSearchLoading}>
                {artistSearchLoading ? 'Searching…' : 'Search'}
              </Button>
            </Stack>

            {artistSearchError && <Alert severity="warning">{artistSearchError}</Alert>}

            {artistSearchResults.map((artist) => {
              const displayName =
                `${artist.first_name || ''} ${artist.last_name || ''}`.trim() ||
                artist.user?.username ||
                `Artist #${artist.id}`;
              return (
                <Box
                  key={artist.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(17,24,39,0.12)',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2">{displayName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {artist.user?.username || artist.username || artist.email || 'Existing ARTflow artist'}
                      </Typography>
                      {artist.email && (
                        <Typography variant="body2" color="text.secondary">
                          {artist.email}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => handleLinkExistingArtist(artist)}
                      disabled={linkingArtistId === artist.id}
                    >
                      {linkingArtistId === artist.id ? 'Linking…' : 'Link Artist'}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkArtistOpen(false)} disabled={Boolean(linkingArtistId)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Modal: Full Artwork view for managed artworks --- */}
      <Dialog
        open={repArtworkModalOpen}
        onClose={() => setRepArtworkModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 2, bgcolor: '#f9fafb' }}>
          {repArtworkLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          )}
          {!repArtworkLoading && !repSelectedArtworkFull && (
            <Typography variant="body2" color="text.secondary">
              Failed to load artwork details.
            </Typography>
          )}
          {!repArtworkLoading && repSelectedArtworkFull && (
            <ArtworkCard
              artwork={repSelectedArtworkFull}
              showActions={true}
              // allow host to edit/delete this represented artwork
              canHostManage={repSelectedArtworkIsRepresented}
              onMarkSold={handleMarkSold}
              allowMarkSold={repSelectedArtworkIsRepresented}
              onDelete={handleManagedArtworkDeleted}
            />
          )}
        </Box>
      </Dialog>

      {/* --- Modal: Add New Artwork via ArtworkCreatePage --- */}
      <Dialog
        open={showAddArtworkModal}
        onClose={() => setShowAddArtworkModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Add New Artwork for{' '}
          {selectedManagedArtist?.display_name ||
            `${selectedManagedArtist?.user?.first_name || ''} ${
              selectedManagedArtist?.user?.last_name || ''
            }`.trim() ||
            'Artist'}
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: '#f9fafb' }}>
          {selectedManagedArtist ? (
            <ArtworkCreatePage
              inline
              hostManaged
              managedArtistId={selectedManagedArtist.id}
              onCreated={(newArtwork) => {
                if (newArtwork) {
                  setRepresentedManagedArtworks((prev) => [newArtwork, ...prev]);
                  setAvailableManagedArtworks((prev) => prev.filter((art) => art.id !== newArtwork.id));
                }
                refreshAgreements().catch(() => {});
                refreshHostedArtworks().catch(() => {});
                setShowAddArtworkModal(false);
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Please select an artist above first.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <ArtworkSearchFiltersModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        statuses={ARTWORK_STATUSES}
        statusFilter={statusFilterNearby}
        onStatusChange={setStatusFilterNearby}
        selectedCategory={selectedCategory}
        onCategoryChange={(value) => {
          setSelectedCategory(value);
          setSelectedSubcategories({});
        }}
        selectedSubcategories={selectedSubcategories}
        onSubcategoryChange={(sub, value) => {
          setSelectedSubcategories((prev) => ({ ...prev, [sub]: value }));
        }}
        minPrice={minPrice}
        maxPrice={maxPrice}
        onMinPriceChange={setMinPrice}
        onMaxPriceChange={setMaxPrice}
        onClearAll={() => {
          setStatusFilterNearby([]);
          setSelectedCategory('');
          setSelectedSubcategories({});
          setMinPrice('');
          setMaxPrice('');
        }}
      />

    </Container>
  );
}
